import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { CountryCode } from '../utils/multiTenancyHeaders';
import { ShopifyTokenGenerator, getShopifyAuthHeader } from '../utils/shopifySessionTokens';
import { QasAddressResponse, QasSearchResponseMessage } from './types';

type QasCountryCode = 'AUE' | 'NZD';

type ConstructorParams =
  | {
      url: string;
      isoCountryCode: CountryCode;
      shopifyTokenGenerator: ShopifyTokenGenerator;
      accessToken?: never;
    }
  | {
      url: string;
      isoCountryCode: CountryCode;
      accessToken: string;
      shopifyTokenGenerator?: never;
    };

export class AddressValidationClient extends ApiClient {
  private readonly _url: string;
  private readonly _accessToken?: string;
  private readonly _shopifyTokenGenerator?: ShopifyTokenGenerator;
  private readonly _isoCountryCode: CountryCode;
  private readonly _qasCountryCode: QasCountryCode;

  constructor({ url, isoCountryCode, accessToken, shopifyTokenGenerator }: ConstructorParams) {
    super();
    this._url = url;
    this._accessToken = accessToken;
    this._isoCountryCode = isoCountryCode;
    this._qasCountryCode = isoCountryCode === 'NZ' ? 'NZD' : 'AUE';
    this._shopifyTokenGenerator = shopifyTokenGenerator;
  }

  generateHeaders = async () => {
    const isInCheckoutExtensibility = this._shopifyTokenGenerator !== undefined;
    let userValidationHeaders: Record<string, string> = {};
    if (isInCheckoutExtensibility) {
      userValidationHeaders = (await getShopifyAuthHeader(this._shopifyTokenGenerator))!;
      return { ...super.requestHeaders, ...userValidationHeaders };
    } else if (this._accessToken) {
      return {
        ...super.requestHeaders,
        'x-jbg-api-key': this._accessToken,
      };
    }
  };

  getQasSearch = async (
    query: string,
    abortSignal: AbortSignal
  ): Promise<ApiResponse<Array<QasSearchResponseMessage>>> => {
    const headers = await this.generateHeaders();
    const path = `${this._url}/search?countryCode=${this._qasCountryCode}&address=${encodeURIComponent(query)}`;

    const { data, statusCode, error } = await this._fetch<any>({
      path,
      method: 'GET',
      abortSignal,
      headers,
    });

    if (error) {
      return { error, statusCode };
    }

    return { data: data.message, error: error, statusCode };
  };

  getQasAddress = async (id: string): Promise<ApiResponse<QasAddressResponse>> => {
    const headers = await this.generateHeaders();
    const path = `${this._url}/address?countryCode=${this._qasCountryCode}&id=${btoa(id)}`;

    const { data, statusCode, error } = await this._fetch<QasAddressResponse>({
      path,
      method: 'GET',
      headers,
    });

    if (error || !data) {
      return { error, statusCode };
    }

    return {
      data: {
        ...data,
        countryCode: this._isoCountryCode,

        message: {
          ...data.message,
          //Assign the CityState to Suburb when NZ has empty Suburb to pass the Shopify form validation
          Suburb: this._isoCountryCode === 'NZ' && !data.message.Suburb ? data.message.CityState : data.message.Suburb,
          Region: this._isoCountryCode === 'NZ' ? data.message.Region : '',
        },
      },
      statusCode,
    };
  };
}
